import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { doc, Firestore, onSnapshot } from '@angular/fire/firestore';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@app/auth/auth.service';
import {
  CompanyGroup,
  CoreConfig,
  FindOptions,
  NotificationDTO,
  PatronNavData,
  System,
  User,
} from '@app/models';
import { constants, utils } from '@app/shared/utils';
import { ModalComponent, ToastService } from '@teorema/patron';
import { MenuItem } from 'primeng/api';
import { filter, Subscription } from 'rxjs';
import { NotificationService } from '../../services/notification.service';
import { PanelConfigurationService } from '../../services/panel-configuration.service';
import { UiService } from '../../services/ui.service';
import { LayoutService } from '../layout/layout.service';

@Component({
  selector: 'patron-layout-v2',
  templateUrl: './layout-v2.component.html',
  styleUrls: ['./layout-v2.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class LayoutV2Component implements OnInit, AfterViewInit, OnDestroy {
  @Input() sidebarVisible: boolean = true;
  @Input() showHeader: boolean = true;
  @Input() navItems: PatronNavData[] = [];
  @Input() isExtendModeNav: boolean = false;

  panelMenuItems: MenuItem[] = [];
  isMobile = false;
  private _sidebarCollapsed = false;
  private _savedSidebarCollapsed: boolean | null = null;
  private readonly debugSidebar = true;
  private readonly mobileBreakpoint = '(max-width: 768px)';

  panelTitle?: string;
  defaultTitle?: string;
  user?: User;
  companyGroup?: CompanyGroup;
  navItemsBottom: PatronNavData[] = [];
  changePasswordConfirmVisible: boolean = false;
  currentTheme?: string;
  loadingTheme: boolean = false;
  config?: CoreConfig;
  loadingConfig: boolean = false;
  visibleDropdowns: any = {};
  notificationsFindOptions: FindOptions = {
    perPage: 15,
  };
  notificationDetailsModalVisible = false;
  currentNotification?: NotificationDTO;
  subscriptions: Subscription[] = [];
  isUser: boolean = false;
  patronUtils = utils;
  userHasAccessToNotifications = false;
  currentVersion?: string;
  updatedVersion?: string;
  modals = new Array(5);
  panelConfig$ = this.panelConfigService.panelConfig$;
  pageTitle$ = this.layoutService.getPageTitle();

  @ViewChild('header') header!: ElementRef;
  @ViewChild('sidebar') sidebar!: ElementRef;
  @ViewChildren('globalModalComponents') modalComponents!: QueryList<ModalComponent>;

  firestore = inject(Firestore);

  constructor(
    public authService: AuthService,
    private router: Router,
    private titleService: Title,
    private activatedRoute: ActivatedRoute,
    private uiService: UiService,
    private renderer: Renderer2,
    private element: ElementRef,
    private breakpointObserver: BreakpointObserver,
    public layoutService: LayoutService,
    private notificationService: NotificationService,
    private toastService: ToastService,
    private panelConfigService: PanelConfigurationService,
    private cdr: ChangeDetectorRef,
    @Inject('config') public coreConfig?: CoreConfig
  ) {
    this.config = authService.getCoreConfig();
    this.panelTitle = this.config.panelTitle;
    this.user = utils.getUser();
    this.companyGroup = utils.getCompanyGroup();

    this.validCustomerAccess();

    this.isMobile = this.isMobileViewport();
    this.logSidebarDebug('constructor:init', { initialIsMobile: this.isMobile });
    this.syncSidebarWithViewport(this.isMobile, 'constructor:init');

    this.subscriptions.push(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => {
          this.logSidebarDebug('router:NavigationEnd', {
            url: event.url,
            urlAfterRedirects: event.urlAfterRedirects,
          });

          this.rebuildPanelMenuItemsFromCurrentState();

          if (this.isMobileViewport()) {
            this.closeSidebar('router:NavigationEnd mobile');
          }
        })
    );
  }

  get sidebarCollapsed(): boolean {
    return this._sidebarCollapsed;
  }

  set sidebarCollapsed(value: boolean) {
    if (this._sidebarCollapsed === value) {
      this.logSidebarDebug('sidebarCollapsed:set ignored same value', { value });
      return;
    }

    const previous = this._sidebarCollapsed;
    this._sidebarCollapsed = value;

    this.logSidebarDebug('sidebarCollapsed:changed', {
      previous,
      current: value,
      stack: this.getDebugStack(),
    });
  }

  // Espelha a largura definida em .sidebar-v2 (layout-v2.component.scss): 224px expandida,
  // 0 colapsada, e sempre 0 em mobile (lá a sidebar é position: fixed e não ocupa espaço no flex).
  get sidebarFlowWidthPx(): number {
    return !this.isMobile && !this.sidebarCollapsed ? 224 : 0;
  }

  toggleSidebar(source: string = 'unknown'): void {
    const nextValue = !this.sidebarCollapsed;

    this.logSidebarDebug('toggleSidebar', {
      source,
      nextValue,
    });

    this.sidebarCollapsed = nextValue;
  }

  openSidebar(source: string = 'unknown'): void {
    this.logSidebarDebug('openSidebar', {
      source,
      willOpen: this.sidebarCollapsed,
    });

    if (this.sidebarCollapsed) {
      this.sidebarCollapsed = false;
    }
  }

  closeSidebar(source: string = 'unknown'): void {
    this.logSidebarDebug('closeSidebar', {
      source,
      willClose: !this.sidebarCollapsed,
    });

    if (!this.sidebarCollapsed) {
      this.sidebarCollapsed = true;
    }
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.matchMedia(this.mobileBreakpoint).matches;
  }

  private syncSidebarWithViewport(isMobile: boolean, source: string): void {
    if (isMobile) {
      this.closeSidebar(`${source}:mobile`);
      return;
    }

    if (this.uiService.sidebarForcedCollapsed) {
      this.closeSidebar(`${source}:forced`);
      return;
    }

    this.openSidebar(`${source}:desktop`);
  }

  private logSidebarDebug(message: string, data: Record<string, unknown> = {}): void {
    if (!this.debugSidebar) return;
  }

  private getDebugStack(): string[] {
    return (new Error().stack ?? '')
      .split('\n')
      .slice(2, 7)
      .map(line => line.trim());
  }

  private getMenuItemKey(label: string, parentKey: string = ''): string {
    return parentKey ? `${parentKey} > ${label}` : label;
  }

  private clearLeafMenuExpansion(items: MenuItem[] = []): void {
    let changed = false;

    const clearItems = (menuItems: MenuItem[] = []) => {
      for (const item of menuItems) {
        if (item.items?.length) {
          clearItems(item.items);
          continue;
        }

        if (item.expanded) {
          item.expanded = false;
          changed = true;
        }
      }
    };

    clearItems(items);

    if (changed) {
      this.panelMenuItems = [...this.panelMenuItems];
      this.cdr.detectChanges();
    }
  }

  private rebuildPanelMenuItemsFromCurrentState(): void {
    if (!this.navItems?.length) {
      this.clearLeafMenuExpansion(this.panelMenuItems);
      return;
    }

    const expandedKeys = this.getExpandedMenuItemKeys(this.panelMenuItems);
    this.panelMenuItems = this.toMenuItems(this.navItems, expandedKeys);
    this.cdr.detectChanges();
  }

  private schedulePanelMenuItemsRebuild(): void {
    setTimeout(() => this.rebuildPanelMenuItemsFromCurrentState());
  }

  private getExpandedMenuItemKeys(items: MenuItem[] = [], parentKey: string = ''): Set<string> {
    const expandedKeys = new Set<string>();

    for (const item of items) {
      if (item.separator || !item.label) continue;

      const key = this.getMenuItemKey(String(item.label), parentKey);

      if (item.items?.length && item.expanded) {
        expandedKeys.add(key);
      }

      if (item.items?.length) {
        for (const childKey of this.getExpandedMenuItemKeys(item.items, key)) {
          expandedKeys.add(childKey);
        }
      }
    }

    return expandedKeys;
  }

  private hasActiveChildRoute(items: PatronNavData[] = []): boolean {
    return items.some(item => {
      if (item.url && this.router.url.startsWith(item.url)) return true;
      return item.children?.length ? this.hasActiveChildRoute(item.children) : false;
    });
  }

  private toMenuItems(
    items: PatronNavData[],
    expandedKeys: Set<string> = new Set(),
    parentKey: string = '',
    depth: number = 1
  ): MenuItem[] {
    const result: MenuItem[] = [];
    for (const item of items) {
      if (item.name.startsWith('__:')) {
        continue;
      }

      const key = this.getMenuItemKey(item.name, parentKey);
      const children = item.children?.length
        ? this.toMenuItems(item.children, expandedKeys, key, depth + 1)
        : undefined;
      const menuItem: MenuItem = {
        label: item.name,
        icon: item.icon ? `bi ${item.icon}` : undefined,
        iconClass: depth === 2 ? 'nav-level-2-icon' : undefined,
        labelClass: item.isLegacyInNav
          ? 'nav-label-badge nav-label-legacy'
          : item.isBetaInNav
            ? 'nav-label-badge nav-label-beta'
            : undefined,
        styleClass: `nav-level-${depth}`,
        routerLink: item.url ?? undefined,
        command:
          item.url && !children?.length ? () => this.schedulePanelMenuItemsRebuild() : undefined,
        items: children,
      };

      if (children?.length && (expandedKeys.has(key) || this.hasActiveChildRoute(item.children))) {
        menuItem.expanded = true;
      }

      result.push(menuItem);
    }
    return result;
  }

  async ngOnInit() {
    this.logSidebarDebug('ngOnInit:start');
    this.userHasAccessToNotifications = this.patronUtils.checkAccess(['Notifications']).result;

    this.subscriptions.push(
      this.layoutService.callUpdateNavItems.subscribe(() => {
        this.logSidebarDebug('layoutService.callUpdateNavItems');
        this.updateNavItems();
      })
    );

    this.subscriptions.push(
      this.uiService.collapseSidebarObs.subscribe(() => {
        this._savedSidebarCollapsed = this._sidebarCollapsed;
        this.closeSidebar('uiService.collapseSidebarObs');
      })
    );

    this.subscriptions.push(
      this.uiService.restoreSidebarObs.subscribe(() => {
        if (this._savedSidebarCollapsed === false) {
          this.openSidebar('uiService.restoreSidebarObs');
        }
        this._savedSidebarCollapsed = null;
      })
    );

    this.subscriptions.push(
      this.uiService.showSidebarObs.subscribe(() => {
        if (!this.uiService.sidebarForcedCollapsed) {
          this.openSidebar('uiService.showSidebarObs');
        }
      })
    );
    this.uiService.showHeaderObs.subscribe(res => (this.showHeader = res));

    if (!this.navItems?.length) {
      await this.updateNavItems();
    }

    if (this.config?.enableSwitchTheme) {
      this.currentTheme = localStorage.getItem(constants.patronThemeKey) ?? 'light';
    }

    this.activatedRoute.data.subscribe((data: any) => {
      if (data.sidebarVisible !== undefined) {
        this.sidebarVisible = data.sidebarVisible;
      }
    });

    this.subscriptions.push(
      this.notificationService.openNotificationEventEmitter.subscribe(
        (notification: NotificationDTO) => {
          this.notificationDetailsModalVisible = false;
          this.currentNotification = notification;
          setTimeout(() => {
            this.notificationDetailsModalVisible = true;
          }, 1);
        }
      )
    );
    this.isUser = utils.isUser();

    if (this.isCustomerUser) {
      this.availableCustomers = this.getAvailableCustomers();
    }

    this.layoutService.setLayoutLoaded();
  }

  ngOnDestroy(): void {
    this.logSidebarDebug('ngOnDestroy');
    this.layoutService.setPageTitle('');
    if (this.defaultTitle) this.titleService.setTitle(this.defaultTitle);
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngAfterViewInit(): void {
    this.logSidebarDebug('ngAfterViewInit:start');
    this.layoutService.setGlobalModals(this.modalComponents);

    this.subscriptions.push(
      this.breakpointObserver
        .observe([this.mobileBreakpoint])
        .subscribe((state: BreakpointState) => {
          this.logSidebarDebug('breakpointObserver:emitted', {
            matches: state.matches,
            breakpoints: state.breakpoints,
            previousIsMobile: this.isMobile,
          });
          this.isMobile = state.matches;
          this.syncSidebarWithViewport(state.matches, 'breakpointObserver');
        })
    );

    setTimeout(() => {
      const needsPasswordReset =
        this.user?.is_temp_password ||
        this.user?.temp_password ||
        this.authService.forcePasswordReset;

      if (needsPasswordReset && !this.authService.changePasswordModalShown) {
        this.authService.changePasswordModalShown = true;
        this.authService.forcePasswordReset = false;
        this.changePasswordConfirmVisible = true;
      }

      const system = utils.getSystem();
      if (system != System.AUDITOR) {
        this.verifyVersion();
      }
    }, 1000);
  }

  async updateNavItems(isUpdated: boolean = false) {
    this.logSidebarDebug('updateNavItems:start', { isUpdated });

    const expandedKeys = this.getExpandedMenuItemKeys(this.panelMenuItems);

    this.navItems = this.authService.getNavItems();
    this.navItemsBottom = this.navItems.filter((value: any, index, arr) => {
      if (value.position == 'bottom') {
        arr.splice(index, 1);
        return true;
      }
      return false;
    });

    this.panelMenuItems = this.toMenuItems(this.navItems, expandedKeys);
    this.logSidebarDebug('updateNavItems:panelMenuItems rebuilt', {
      isUpdated,
      expandedKeys: Array.from(expandedKeys),
      navItemsCount: this.navItems.length,
      navItemsBottomCount: this.navItemsBottom.length,
      panelMenuItemsCount: this.panelMenuItems.length,
    });

    if (!isUpdated && this.coreConfig?.apiUrl) {
      if (utils.getSystem() != System.AUDITOR) {
        this.logSidebarDebug('updateNavItems:authService.me start');
        await this.authService.me().then(user => {
          this.logSidebarDebug('updateNavItems:authService.me resolved', {
            hasUser: !!user,
          });
          if (user) {
            this.user = user;
            utils.setUser(this.user);
            this.updateNavItems(true);
            if (user.licences_validation) {
              this.toastService.show({ color: 'error', message: user.licences_validation });
            }
          }
        });
      }
    }
  }

  dropdown!: HTMLElement | null;

  toggleDropdown(id: number) {
    this.dropdown = document.getElementById(id.toString());
    this.dropdown?.toggleAttribute('hidden');
  }

  getFirstLetter(text: string) {
    return text.substring(0, 1).toUpperCase();
  }

  logout() {
    this.authService.logout();
  }

  onCancelChangePassword() {
    this.changePasswordConfirmVisible = false;
  }

  goToChangePassword() {
    this.router.navigate(['alterar-senha']);
    this.changePasswordConfirmVisible = false;
  }

  dropdownVisibleChange(key: string, visible: boolean) {
    this.visibleDropdowns[key] = visible;

    if (this.header?.nativeElement) {
      this.renderer.removeClass(this.header.nativeElement, 'bring-to-front');

      if (Object.values(this.visibleDropdowns).includes(true))
        this.renderer.addClass(this.header.nativeElement, 'bring-to-front');
    }
  }

  /** Chamado #0000577675 - aviso de usuario sem licenca. Ver utils.hasNoLicenseInCurrentGroup(). */
  get noLicenseInCurrentGroup(): boolean {
    return utils.hasNoLicenseInCurrentGroup();
  }

  get currentAccessType() {
    const key = utils.getAccessType();
    if (!key) return;
    return { key, label: utils.getAccessTypeLabel(key) };
  }

  accessModalVisible: boolean = false;
  availableCustomers: any[] = [];

  openAccessModal() {
    this.accessModalVisible = false;
    setTimeout(() => {
      this.accessModalVisible = true;
    });
  }

  changeAccess() {
    this.router.navigate([this.config?.redirectPathAfterLogin]);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }

  getAvailableCustomers() {
    if (!this.user?.bw_user_company_groups?.length) return [];

    const currentCompanyGroupId = this.companyGroup?.id;

    const customerAccesses = this.user.bw_user_company_groups.filter(
      (bwcg: any) =>
        bwcg.company_group_id === currentCompanyGroupId &&
        bwcg.customer_id &&
        bwcg.customer?.person?.name
    );

    const uniqueCustomers = customerAccesses.reduce((acc: any[], current: any) => {
      const exists = acc.find(item => item.customer_id === current.customer_id);
      if (!exists) {
        acc.push(current);
      }
      return acc;
    }, []);

    return uniqueCustomers;
  }

  switchCustomer(bwUserCompanyGroup: any) {
    if (!bwUserCompanyGroup?.customer_id || !this.user) return;

    const newCompanyGroup = {
      ...this.companyGroup,
      pivot: {
        ...this.companyGroup?.pivot,
        customer_id: bwUserCompanyGroup.customer_id,
        user_id: bwUserCompanyGroup.user_id,
      },
    };

    utils.setCompanyGroup(newCompanyGroup);
    utils.setUser(this.user);

    this.visibleDropdowns['customerSwitch'] = false;

    setTimeout(() => {
      this.router.navigate(['/dashboard']).then(() => {
        window.location.reload();
      });
    }, 300);
  }

  get isCustomerUser(): boolean {
    return utils.isCustomer();
  }

  get hasMultipleCustomers(): boolean {
    return this.isCustomerUser && this.availableCustomers.length > 1;
  }

  async validCustomerAccess() {
    if (utils.getSystem() != System.AUDITOR) {
      await this.authService.me().then(user => {
        let access = user?.bw_user_company_groups?.find((i: any) => {
          if (i.company_group_id != this.companyGroup?.id) return false;

          if (this.companyGroup?.pivot?.customer_id && i.customer_id) {
            return i.customer_id === this.companyGroup.pivot.customer_id;
          }

          if (this.companyGroup?.pivot?.user_id && i.user_id) {
            return i.user_id === this.companyGroup.pivot.user_id;
          }

          return true;
        });
        if (utils.isCustomer() && !access?.customer_id) {
          this.toastService.show({ color: 'warn', message: 'Acesso removido!' });
          this.authService.logout();
        }
        if (user.licences_validation) {
          this.toastService.show({ color: 'error', message: user.licences_validation });
        }
      });
    }
  }

  goToNotificationsPage() {
    this.router.navigate(['/notificacoes']);
  }

  runNotificationAction() {
    this.notificationService.notificationActionEventEmitter.emit(this.currentNotification);
    this.notificationDetailsModalVisible = false;
  }

  get authCustomer() {
    return utils.getCustomer();
  }

  get authSupplier() {
    return utils.getSupplier();
  }

  get isV3ndorModule(): boolean {
    return this.router.url?.startsWith('/v3ndor') || false;
  }

  isActiveNavItem(navItem: PatronNavData) {
    if (navItem.children?.length) {
      return (
        navItem.children.findIndex(
          children => children.url && this.router.url.startsWith(children.url)
        ) >= 0
      );
    }
    return navItem.url && this.router.url.startsWith(navItem.url);
  }

  get hasCompaniesPermission() {
    return utils.checkAccess(['Companies']).result;
  }

  get isOutdatedVersion() {
    return this.currentVersion && this.updatedVersion && this.currentVersion != this.updatedVersion;
  }

  verifyVersion() {
    this.currentVersion = this.coreConfig?.appVersion;
    const env = this.coreConfig?.env;

    if (!env) {
      console.warn('No environment defined in core config');
      return;
    }

    try {
      const docRef = doc(this.firestore, 'info', env);

      const unsubscribe = onSnapshot(
        docRef,
        docSnap => {
          if (docSnap.exists() && docSnap.data()?.['version']) {
            this.updatedVersion = docSnap.data()?.['version'];
          } else {
            this.updatedVersion = undefined;
          }
          this.cdr.detectChanges();
        },
        error => {
          console.error('Error listening to version updates:', error);
          this.updatedVersion = undefined;
          this.cdr.detectChanges();
        }
      );

      this.subscriptions.push({ unsubscribe } as any);
    } catch (error) {
      console.error('Error setting up version listener:', error);
    }
  }

  reloadPage(): void {
    const timestamp = new Date().getTime();
    const currentUrl = `${location.origin}${location.pathname}`;
    location.href = `${currentUrl}?tmp=${timestamp}`;
  }
}
